import { Link } from "react-router-dom";
import { Icon } from "@/lib/ui";

const CATS = [
  { icon: "Briefcase", label: "Luggage" }, { icon: "PawPrint", label: "Pets" },
  { icon: "ShoppingBag", label: "Bags" }, { icon: "KeyRound", label: "Keys" },
  { icon: "FileText", label: "Documents" }, { icon: "Car", label: "Vehicles" },
  { icon: "Laptop", label: "Electronics" }, { icon: "Tag", label: "More" },
];

const STEPS = [
  { icon: "QrCode", title: "Attach the tag", body: "Stick or clip the QR tag onto anything you'd hate to lose." },
  { icon: "ShieldCheck", title: "Claim it privately", body: "Activate with your secret code. Your number stays hidden by default." },
  { icon: "MessageSquareHeart", title: "Get it back", body: "A finder scans, sends a message, and reaches you — no details exposed." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-border">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
              <Icon name="ScanLine" className="w-5 h-5" />
            </span>
            Scan2Return
          </div>
          <div className="flex items-center gap-2">
            <Link data-testid="nav-login" to="/login" className="tap px-4 py-2 text-sm font-semibold rounded-full hover:bg-secondary">Log in</Link>
            <Link data-testid="nav-register" to="/register" className="tap px-4 py-2 text-sm font-semibold rounded-full bg-primary text-primary-foreground hover:opacity-90">Get started</Link>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-5 pt-16 pb-14 md:pt-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-rise">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-status-active bg-[#EAF3FF] px-3 py-1.5 rounded-full">
              <Icon name="Radar" className="w-3.5 h-3.5" /> Lost &amp; Found, reinvented
            </span>
            <h1 className="font-display font-black tracking-tight text-4xl md:text-6xl mt-5 leading-[1.02]">
              A secure QR identity for everything you own.
            </h1>
            <p className="text-muted-foreground text-lg mt-5 max-w-md">
              Tag your luggage, pets, keys and gadgets. If they're ever lost, a finder reaches you instantly — while your phone number stays private.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link data-testid="hero-register" to="/register" className="tap px-8 py-4 rounded-full bg-primary text-primary-foreground font-bold hover:opacity-90 flex items-center gap-2">
                Claim your first tag <Icon name="ArrowRight" className="w-4 h-4" />
              </Link>
              <Link data-testid="hero-login" to="/login" className="tap px-8 py-4 rounded-full border border-border font-bold hover:bg-secondary">I have an account</Link>
            </div>
          </div>

          <div className="relative animate-rise" style={{ animationDelay: "0.1s" }}>
            <div className="mx-auto w-full max-w-[300px] rounded-[2rem] border border-border bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)] overflow-hidden">
              <div className="bg-status-lost px-6 pt-10 pb-8 text-white text-center">
                <Icon name="TriangleAlert" className="w-14 h-14 mx-auto" />
                <p className="font-display font-black text-2xl mt-3">Reported Lost</p>
                <p className="text-white/80 text-sm mt-1">QT-PET-000001 · Pet Tag</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="rounded-xl bg-secondary p-4 text-sm">
                  "Please contact me if you found Bruno. Reward offered!"
                </div>
                <div className="w-full py-3.5 rounded-full bg-primary text-primary-foreground text-center font-bold">Contact Owner</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-10 border-t border-border">
        <h2 className="font-display font-bold text-2xl tracking-tight mb-8">How it works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} className="rounded-2xl border border-border p-6 tap hover:-translate-y-0.5 transition-transform">
              <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center mb-4">
                <Icon name={s.icon} className="w-5 h-5" />
              </div>
              <p className="font-display font-bold text-lg">{s.title}</p>
              <p className="text-muted-foreground text-sm mt-1.5">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-10 border-t border-border">
        <h2 className="font-display font-bold text-2xl tracking-tight mb-6">Tag anything</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CATS.map((c) => (
            <div key={c.label} className="rounded-2xl border border-border p-5 flex items-center gap-3 tap hover:bg-secondary">
              <Icon name={c.icon} className="w-5 h-5" />
              <span className="font-semibold text-sm">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border mt-8">
        <div className="max-w-5xl mx-auto px-5 py-8 text-sm text-muted-foreground flex flex-wrap items-center justify-between gap-3">
          <span className="font-display font-bold text-foreground">Scan2Return</span>
          <span>Privacy-first lost &amp; found · {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
